import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    FormControl,
    FormControlLabel,
    Icon,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import React from 'react';

import { CrisBrandPicker } from 'app/components/cris-brand-picker';
import { ChannelSelector } from 'app/components/form-controls/channel-selector';
import { If } from 'app/components/if';
import { Translate } from 'app/components/translate';
import { CrisEntityAction } from 'app/constants/cris-entity-action';
import { GeneralConstants } from 'app/constants/general-constants';
import { StoreType } from 'app/constants/store-type';
import { Brand } from 'app/model/cris/brand';
import { Store } from 'app/model/cris/store';
import { ModelWithoutId } from 'app/type/model-with-id';
import { InputOnChangeNormalizer } from 'app/utils/input-on-change.normalizer';

interface StoreActionProps {
    brand: ModelWithoutId<Brand>;
    storeData: ModelWithoutId<Store>;
    disabled?: boolean;

    /**
     * Store data changed.
     */
    onChange: (store?: ModelWithoutId<Store>) => void;

    onDelete: () => void;
}

interface StoreActionState {
    action: CrisEntityAction;
}

export class StoreAction extends React.Component<StoreActionProps, StoreActionState> {
    private storeDataHandler: InputOnChangeNormalizer;

    public constructor(props: Readonly<StoreActionProps> | StoreActionProps) {
        super(props);

        this.state = {
            action: props.storeData.id ? CrisEntityAction.EDIT : CrisEntityAction.CREATE,
        };

        this.storeDataHandler = new InputOnChangeNormalizer((name, value) => {
            props.onChange({
                ...this.props.storeData,
                [name]: value,
            });
        });
    }

    public render(): React.ReactNode {
        const disabled = this.props.disabled;

        return (
            <Card elevation={4} className='mb-2'>
                <CardHeader
                    title={`${this.state.action === CrisEntityAction.EDIT ? 'Edit' : 'Create'} Store`}
                    action={
                        <RadioGroup
                            row
                            value={this.state.action}
                            onChange={(evt) => {
                                this.changeAction(evt.target.value as CrisEntityAction);
                            }}
                        >
                            <FormControlLabel
                                value={CrisEntityAction.CREATE}
                                control={<Radio />}
                                label='New'
                                disabled={disabled}
                            />
                            <FormControlLabel
                                value={CrisEntityAction.EDIT}
                                control={<Radio />}
                                label='Existing'
                                disabled={undefined === this.props.brand.id || disabled}
                            />
                            <If condition={!disabled}>
                                <Tooltip title='Remove Action'>
                                    <IconButton aria-label='delete' onClick={() => this.props.onDelete()} size='large'>
                                        <Icon color='secondary'>delete</Icon>
                                    </IconButton>
                                </Tooltip>
                            </If>
                        </RadioGroup>
                    }
                />
                <CardContent>{this.getStoreForm()}</CardContent>
            </Card>
        );
    }

    public componentDidUpdate(prevProps: Readonly<StoreActionProps>): void {
        // when brand gets changed from existing to new all stores must be of type create.
        if (CrisEntityAction.EDIT === this.state.action && !this.props.brand.id) {
            this.changeAction(CrisEntityAction.CREATE);
        }

        // when stuff get deleted from storeData array, each component here gets refreshed with new data and we need
        // to recompute proper action type based on the store's id
        if (this.props.storeData.id === prevProps.storeData.id) {
            return;
        }

        if (CrisEntityAction.EDIT !== this.state.action && this.props.storeData.id) {
            this.changeAction(CrisEntityAction.EDIT);
        }

        if (CrisEntityAction.EDIT === this.state.action && !this.props.storeData.id) {
            this.changeAction(CrisEntityAction.CREATE);
        }
    }

    private getStoreForm() {
        let storePicker: React.ReactNode;

        const storeOptions = this.props.brand.stores || [];
        const storeId = this.props.storeData.id;
        const storeValue = storeId
            ? storeOptions.find((store) => store.id === storeId) || GeneralConstants.COMPONENT_VALUE_NULL
            : GeneralConstants.COMPONENT_VALUE_NULL;

        if (CrisEntityAction.EDIT === this.state.action) {
            storePicker = (
                <Box display='flex' flexDirection='row' mt={1} alignItems='center' flexWrap='wrap'>
                    <Box flexBasis='20rem' flexShrink={1} mr={2}>
                        <Autocomplete
                            value={storeValue}
                            onChange={(evt, newValue) => this.props.onChange(newValue || undefined)}
                            options={storeOptions}
                            getOptionLabel={(store) => store.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} label='Target Store' variant='standard' />}
                        />
                    </Box>
                    <Box flexBasis='20rem' flexShrink={1} mr={2}>
                        <CrisBrandPicker
                            name='brand'
                            value={this.props.storeData.brand}
                            label='Change Brand'
                            up
                            onChange={this.storeDataHandler.onChange}
                        />
                    </Box>
                    <Typography>
                        You can move store to another brand by selecting a new brand here, leave blank to keep under
                        current brand.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box display='flex' flexDirection='column'>
                {storePicker}
                <Box display='flex' flexDirection='row' mt={1} flexWrap='wrap' alignItems='center'>
                    <Box flexBasis='15rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:New Store Name' />}
                            fullWidth
                            name='name'
                            onChange={(p) =>
                                this.storeDataHandler.onChange({
                                    target: { name: 'name', value: p.target.value.toUpperCase() },
                                })
                            }
                            value={this.props.storeData.name.toUpperCase() || ''}
                            required
                        />
                    </Box>
                    <Box flexBasis='8rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:New Store Num' />}
                            fullWidth
                            name='storeNum'
                            onChange={this.storeDataHandler.onChange}
                            value={this.props.storeData.storeNum || ''}
                        />
                    </Box>
                    <Box flexBasis='5rem' mr={2} mb={2}>
                        <FormControl variant='standard' fullWidth>
                            <InputLabel>Type</InputLabel>
                            {/* TODO make a form component to handle enums automatically */}
                            <Select
                                name='type'
                                value={this.props.storeData.type || ''}
                                onChange={this.storeDataHandler.onChange}
                                label='Type'
                            >
                                <MenuItem value={StoreType.NONE}>(none)</MenuItem>
                                <MenuItem value={StoreType.DIST}>Distribution</MenuItem>
                                <MenuItem value={StoreType.PAYMENT}>Payment</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box flexBasis='6rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Location ID' />}
                            fullWidth
                            name='locationId'
                            onChange={this.storeDataHandler.onChange}
                            type='number'
                            value={this.props.storeData.locationId || ''}
                        />
                    </Box>
                    <Box flexBasis='20rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Domain' />}
                            fullWidth
                            name='domain'
                            onChange={this.storeDataHandler.onChange}
                            value={this.props.storeData.domain || ''}
                        />
                    </Box>
                    <Box flexBasis='7rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Phone' />}
                            fullWidth
                            name='phone'
                            onChange={this.storeDataHandler.onChange}
                            value={this.props.storeData.phone || ''}
                        />
                    </Box>
                    <Box flexBasis='8rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Store Owner ID' />}
                            fullWidth
                            name='storeownerId'
                            onChange={this.storeDataHandler.onChange}
                            type='number'
                            value={this.props.storeData.storeownerId || ''}
                        />
                    </Box>
                    <Box flexBasis='12rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:MID Given' />}
                            fullWidth
                            name='midGiven'
                            onChange={this.storeDataHandler.onChange}
                            value={this.props.storeData.midGiven || ''}
                        />
                    </Box>
                    <Box flexBasis='10rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Date Opened' />}
                            type='date'
                            name='dateOpened'
                            onChange={this.storeDataHandler.onChange}
                            value={
                                this.props.storeData.dateOpened
                                    ? format(parseISO(this.props.storeData.dateOpened), 'yyyy-MM-dd')
                                    : ''
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Box>

                    <Box flexBasis='10rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Date Closed' />}
                            type='date'
                            name='dateClosed'
                            onChange={this.storeDataHandler.onChange}
                            value={
                                this.props.storeData.dateClosed
                                    ? format(parseISO(this.props.storeData.dateClosed), 'yyyy-MM-dd')
                                    : ''
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Box>
                    <Box flexBasis='12rem' mr={2} mb={2}>
                        <TextField
                            label={<Translate tkey='forms:Source' />}
                            fullWidth
                            name='source'
                            onChange={this.storeDataHandler.onChange}
                            value={this.props.storeData.source || ''}
                        />
                    </Box>
                    <Box flexBasis='12rem' mr={2} mb={2}>
                        <ChannelSelector
                            label='Default Channel'
                            name='defaultChannel'
                            onChange={this.storeDataHandler.onChange}
                            value={this.props.storeData.defaultChannel}
                        />
                    </Box>
                    <Box flexBasis='10rem' mr={2} mb={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={this.props.storeData.isDefault || false}
                                    onChange={this.storeDataHandler.onCheckboxChange}
                                    name='isDefault'
                                />
                            }
                            label='Default Store'
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    private changeAction(action: CrisEntityAction) {
        this.setState({ action });

        if (action === CrisEntityAction.CREATE) {
            this.props.onChange({
                ...this.props.storeData,
                brand: undefined,
                id: undefined,
            });
        }
    }
}
